/* File: src\components\dashboard\utils\statsCalculator.js */

/* src/components/dashboard/utils/statsCalculator.js */

import { formatCombinedDifficulty } from '../../../contexts/gameHelpers';
import { formatTotalDuration } from './formatters'; // Assuming formatters is in the same dir

/**
 * Calculates aggregated statistics for a given array of sessions.
 * @param {Array} sessions - An array of session objects.
 * @returns {object|null} An object containing calculated stats or null if no sessions provided.
 */
export const calculateSessionStats = (sessions) => {
  if (!Array.isArray(sessions) || sessions.length === 0) {
    return null;
  }

  const filteredSessions = sessions.filter(Boolean); // Ensure no null/undefined entries

  if (filteredSessions.length === 0) {
      return null;
  }

  // --- Initialize Calculation Variables ---
  const totalSessions = filteredSessions.length;
  let totalDurationMs = 0;
  let totalAccuracy = 0;
  let totalVisualAccuracy = 0;
  let totalAudioAccuracy = 0;
  let totalDprime = 0;
  let totalStartLevel = 0;
  let highestEndLevel = -Infinity;
  let lowestStartLevel = Infinity;
  let validAccuracyCount = 0;
  let validVisualAccCount = 0;
  let validAudioAccCount = 0;
  let validDprimeCount = 0;
  let validStartLevelCount = 0;
  let validEndLevelCount = 0;

  // --- Iterate and Calculate Metrics ---
  filteredSessions.forEach(session => {
    if (typeof session.durationMs === 'number' && isFinite(session.durationMs)) totalDurationMs += session.durationMs;
    if (typeof session.accuracy === 'number' && isFinite(session.accuracy)) { totalAccuracy += session.accuracy; validAccuracyCount++; }
    if (typeof session.visualAccuracy === 'number' && isFinite(session.visualAccuracy)) { totalVisualAccuracy += session.visualAccuracy; validVisualAccCount++; }
    if (typeof session.audioAccuracy === 'number' && isFinite(session.audioAccuracy)) { totalAudioAccuracy += session.audioAccuracy; validAudioAccCount++; }
    if (typeof session.overallDprime === 'number' && isFinite(session.overallDprime)) { totalDprime += session.overallDprime; validDprimeCount++; }

    const startN = session.nBack;
    const startDiff = session.difficulty ?? 0;
    if (typeof startN === 'number' && typeof startDiff === 'number' && isFinite(startN) && isFinite(startDiff)) {
      const clampedStartDiff = Math.max(0, Math.min(0.95, startDiff));
      const startLevel = startN + clampedStartDiff;
      totalStartLevel += startLevel;
      lowestStartLevel = Math.min(lowestStartLevel, startLevel);
      validStartLevelCount++;
    }

    const endN = session.newNBack ?? startN;
    const endDiff = session.newDifficulty ?? startDiff;
    if (typeof endN === 'number' && typeof endDiff === 'number' && isFinite(endN) && isFinite(endDiff)) {
        const clampedEndDiff = Math.max(0, Math.min(0.95, endDiff));
        const endLevel = endN + clampedEndDiff;
        highestEndLevel = Math.max(highestEndLevel, endLevel);
        validEndLevelCount++;
    }
  });

  // --- Calculate Averages (handle division by zero) ---
  const avgAccuracy = validAccuracyCount > 0 ? totalAccuracy / validAccuracyCount : null;
  const avgVisualAccuracy = validVisualAccCount > 0 ? totalVisualAccuracy / validVisualAccCount : null;
  const avgAudioAccuracy = validAudioAccCount > 0 ? totalAudioAccuracy / validAudioAccCount : null;
  const avgDprime = validDprimeCount > 0 ? totalDprime / validDprimeCount : null;
  const avgStartLevel = validStartLevelCount > 0 ? totalStartLevel / validStartLevelCount : null;

  // --- Helper to format combined level values ---
  const formatLevel = (combinedLevel) => {
      if (combinedLevel === -Infinity || combinedLevel === Infinity || !isFinite(combinedLevel) || combinedLevel === null) return 'N/A';
      const nBack = Math.floor(combinedLevel);
      const difficulty = Math.max(0, combinedLevel - nBack); // Ensure difficulty isn't negative
      return formatCombinedDifficulty(nBack, difficulty);
  };

   // Ensure lowestStartLevel is finite before formatting
   const formattedLowestStartLevel = isFinite(lowestStartLevel) ? formatLevel(lowestStartLevel) : 'N/A';

  // --- Return the Calculated Stats ---
  return {
    totalSessions, // Already included
    totalDurationMs,
    avgAccuracy,
    avgVisualAccuracy,
    avgAudioAccuracy,
    avgDprime,
    avgStartLevel: formatLevel(avgStartLevel),
    highestEndLevel: formatLevel(highestEndLevel),
    lowestStartLevel: formattedLowestStartLevel, // Use the potentially adjusted value
  };
};

/**
 * Formats the calculated stats for display.
 * @param {object|null} stats - The stats object returned by calculateSessionStats.
 * @returns {object|null} An object with formatted values or null.
 */
export const formatCalculatedStats = (stats) => {
    if (!stats) return null;

    const formatNum = (num, decimals = 1) => (num !== null && num !== undefined && Number.isFinite(num) ? num.toFixed(decimals) : 'N/A');
    const formattedTotalTime = formatTotalDuration(stats.totalDurationMs);

    return {
        ...stats, // Include original calculated values (including totalSessions)
        // Add formatted values
        formattedTotalTime,
        formattedAvgAccuracy: `${formatNum(stats.avgAccuracy, 1)}%`,
        formattedAvgVisualAccuracy: `${formatNum(stats.avgVisualAccuracy, 1)}%`,
        formattedAvgAudioAccuracy: `${formatNum(stats.avgAudioAccuracy, 1)}%`,
        formattedAvgDprime: formatNum(stats.avgDprime, 2),
        // Levels are already formatted by calculateSessionStats
    };
}